import styled from '@emotion/styled'
import type { FC } from 'react'

import Divider from '@segment/matcha/components/Divider'
import ColumnTrack from '@segment/matcha/components/ColumnTrack'

import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'

import Badge from '@segment/matcha/components/Badge'

import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'
import CardBase, * as Card from '@segment/matcha/recipes/Card'
import AccordionBase, * as Accordion from '@segment/matcha/recipes/Accordion'

import { useActiveState } from '@segment/utils/useActiveState'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

import type { TitleWithFAQs as TitleWithFAQsProps, GroupOfFAQs as GroupProps, SingleFAQ } from '../../data'

const TitleWithFAQs: FC<Omit<TitleWithFAQsProps, `type`>> = ({ title, slug, groups }) => (
  <Wrapper id={slug}>
    <CopyTextBase>
      <CopyText.Heading tag="h2" variant="h2">
        {title}
      </CopyText.Heading>
    </CopyTextBase>
    <StyledDivider />
    <StyledColumnTrack>
      {groups.map((props, key) => (
        <Group {...props} key={key} />
      ))}
    </StyledColumnTrack>
  </Wrapper>
)

const Group: FC<GroupProps> = ({ title, faqs }) => (
  <GroupWrapper>
    <CopyText.Heading tag="h3" variant="h2">
      {title}
    </CopyText.Heading>
    <FAQs faqs={faqs} />
  </GroupWrapper>
)

interface FAQsProps {
  faqs: SingleFAQ[]
}

const FAQs: FC<FAQsProps> = ({ faqs }) => {
  const pageConfig = usePageConfig()
  const items = faqs.map(() => false)
  const { activeItems, toggle } = useActiveState(items, {
    allowMultipleActive: true,
  })
  return (
    <>
      {activeItems.map((active, index) => (
        <AccordionBase key={`${faqs[index].title}-${index}`}>
          <Accordion.Header onClick={() => toggle(index)}>
            <Accordion.TitleWrapper>
              <Accordion.Title opened={active}>{faqs[index].title}</Accordion.Title>
              <Accordion.SubTitle>{faqs[index].description}</Accordion.SubTitle>
            </Accordion.TitleWrapper>
            <Accordion.Icon opened={active} />
          </Accordion.Header>
          <Accordion.Body>
            {active && (
              <StyledAccordionContentWrapper>
                <ColumnTrack
                  overrides={{
                    md: 2,
                  }}
                >
                  {faqs[index].cards.map(({ title, description, featured, href }, key) => (
                    <CardBase href={getAbsoluteUrl(href, pageConfig)} key={key}>
                      <Card.Body>
                        {featured && (
                          <Card.Accessories>
                            <Card.AccessoriesRow>
                              <Badge variant="filledBlue">NEW</Badge>
                            </Card.AccessoriesRow>
                          </Card.Accessories>
                        )}
                        <Card.Copy>
                          <Card.Headline>{title}</Card.Headline>
                          <Card.Description>{description}</Card.Description>
                        </Card.Copy>
                        <Card.Action alignment="right">
                          <ChevronRightIcon />
                        </Card.Action>
                      </Card.Body>
                    </CardBase>
                  ))}
                </ColumnTrack>
              </StyledAccordionContentWrapper>
            )}
          </Accordion.Body>
        </AccordionBase>
      ))}
    </>
  )
}

const StyledAccordionContentWrapper = styled(Accordion.ContentWrapper)`
  overflow: initial;
`

const StyledColumnTrack = styled(ColumnTrack)`
  gap: 56px;
`

const StyledDivider = styled(Divider)`
  margin: 16px 0 18px;

  ${({ theme }) => theme.breakpoints.up(`md`)} {
    margin: 16px 0 32px;
  }
`

const GroupWrapper = styled.div``

const Wrapper = styled.div``

export default TitleWithFAQs
